import {
  Bell,
  Blocks,
  Database,
  Gauge,
  Home,
  LineChart,
  ListChecks,
  RefreshCw,
  Save,
  Settings
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { getDataStatus } from "../data/api";
import type { DataStatusPayload } from "../data/api";

export type RouteKey = "dashboard" | "monitoring" | "plans" | "execution" | "alerts" | "settings" | "health";

interface AppShellProps {
  route: RouteKey;
  onRouteChange: (route: RouteKey) => void;
  children: ReactNode;
}

const nav = [
  { route: "dashboard", label: "Dashboard", icon: Home },
  { route: "monitoring", label: "Monitoring", icon: LineChart },
  { route: "plans", label: "Plans", icon: Blocks },
  { route: "execution", label: "Execution", icon: ListChecks },
  { route: "alerts", label: "Alerts", icon: Bell },
  { route: "settings", label: "Settings", icon: Settings }
] as const;

export default function AppShell({ route, onRouteChange, children }: AppShellProps) {
  const [marketDataStatus, setMarketDataStatus] = useState<DataStatusPayload | null>(null);
  const [dataError, setDataError] = useState(false);
  const [saveFlash, setSaveFlash] = useState<"idle" | "saving" | "saved" | "failed">("idle");
  const [refreshFlash, setRefreshFlash] = useState<"idle" | "refreshing" | "done" | "failed">("idle");

  useEffect(() => {
    let mounted = true;
    const refreshDataStatus = () => {
      getDataStatus()
        .then((status) => {
          if (!mounted) return;
          setMarketDataStatus(status);
          setDataError(false);
        })
        .catch(() => {
          if (!mounted) return;
          setDataError(true);
        });
    };

    const onDataRefreshed = () => refreshDataStatus();
    refreshDataStatus();
    const interval = window.setInterval(refreshDataStatus, 60_000);
    window.addEventListener("cp-dashboard:refresh-done", onDataRefreshed);
    return () => {
      mounted = false;
      window.clearInterval(interval);
      window.removeEventListener("cp-dashboard:refresh-done", onDataRefreshed);
    };
  }, []);

  useEffect(() => {
    const onRefreshStarted = () => setRefreshFlash("refreshing");
    const onRefreshDone = () => {
      setRefreshFlash("done");
      window.setTimeout(() => setRefreshFlash("idle"), 1400);
    };
    const onRefreshFailed = () => {
      setRefreshFlash("failed");
      window.setTimeout(() => setRefreshFlash("idle"), 2400);
    };
    window.addEventListener("cp-dashboard:refresh-started", onRefreshStarted);
    window.addEventListener("cp-dashboard:refresh-done", onRefreshDone);
    window.addEventListener("cp-dashboard:refresh-failed", onRefreshFailed);
    return () => {
      window.removeEventListener("cp-dashboard:refresh-started", onRefreshStarted);
      window.removeEventListener("cp-dashboard:refresh-done", onRefreshDone);
      window.removeEventListener("cp-dashboard:refresh-failed", onRefreshFailed);
    };
  }, []);

  useEffect(() => {
    const onSaveStarted = () => setSaveFlash("saving");
    const onSaveDone = () => {
      setSaveFlash("saved");
      window.setTimeout(() => setSaveFlash("idle"), 1400);
    };
    const onSaveFailed = () => {
      setSaveFlash("failed");
      window.setTimeout(() => setSaveFlash("idle"), 2200);
    };

    window.addEventListener("cp-dashboard:save-started", onSaveStarted);
    window.addEventListener("cp-dashboard:save-done", onSaveDone);
    window.addEventListener("cp-dashboard:save-failed", onSaveFailed);
    return () => {
      window.removeEventListener("cp-dashboard:save-started", onSaveStarted);
      window.removeEventListener("cp-dashboard:save-done", onSaveDone);
      window.removeEventListener("cp-dashboard:save-failed", onSaveFailed);
    };
  }, []);

  const dataStatus = useMemo(() => buildDataStatus(marketDataStatus, dataError), [dataError, marketDataStatus]);
  const requestSave = () => {
    window.dispatchEvent(new CustomEvent("cp-dashboard:force-save"));
  };
  const requestRefresh = () => {
    setRefreshFlash("refreshing");
    window.dispatchEvent(new CustomEvent("cp-dashboard:refresh-data"));
  };

  return (
    <div className="app">
      <aside className="rail" aria-label="Primary navigation">
        <div className="logo" aria-label="CP Dashboard">
          <CPLogoMark />
        </div>
        <nav className="rail-nav">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.route}
                className={`rail-button ${route === item.route ? "is-active" : ""}`}
                onClick={() => onRouteChange(item.route)}
                title={item.label}
                aria-label={item.label}
              >
                <Icon />
                <span>{item.label}</span>
              </button>
            );
          })}
          <button className={`rail-button ${route === "health" ? "is-active" : ""}`} title="System health" aria-label="System health" onClick={() => onRouteChange("health")}>
            <Gauge />
            <span>Health</span>
          </button>
        </nav>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <div className="title-line">
            <h1>Contingency Plan Dashboard</h1>
          </div>
          <div className="top-actions">
            <span className="data-status-line" title={dataStatus.detail}>
              <span className={`data-status-dot ${dataStatus.tone}`} aria-label={`Data status ${dataStatus.tone}`} />
              <Database />
              Data: <b>Latest Update {dataStatus.label}</b>
            </span>
            {route === "dashboard" && (
              <button className={`top-save-btn top-refresh-btn ${refreshFlash !== "idle" ? `is-${refreshFlash}` : ""}`} type="button" onClick={requestRefresh} disabled={refreshFlash === "refreshing"}>
                <RefreshCw />
                {refreshFlash === "refreshing" ? "Refreshing" : refreshFlash === "done" ? "Updated" : refreshFlash === "failed" ? "Retry" : "Refresh Data"}
              </button>
            )}
            {(route === "dashboard" || route === "plans") && (
              <button className={`top-save-btn ${saveFlash !== "idle" ? `is-${saveFlash}` : ""}`} type="button" onClick={requestSave}>
                <Save />
                {saveFlash === "saving" ? "Saving" : saveFlash === "saved" ? "Saved" : saveFlash === "failed" ? "Failed" : "Save"}
              </button>
            )}
          </div>
        </header>
        {children}
      </main>
    </div>
  );
}

function CPLogoMark() {
  return (
    <svg className="logo-mark" viewBox="0 0 120 82" role="img" aria-label="CP Electric Cyan logo">
      <defs>
        <linearGradient id="cp-logo-shell" x1="18" x2="102" y1="8" y2="74" gradientUnits="userSpaceOnUse">
          <stop stopColor="#35f3ff" />
          <stop offset="0.52" stopColor="#0f73ff" />
          <stop offset="1" stopColor="#9b4de3" />
        </linearGradient>
        <linearGradient id="cp-logo-text" x1="30" x2="93" y1="23" y2="61" gradientUnits="userSpaceOnUse">
          <stop stopColor="#54f6ff" />
          <stop offset="0.45" stopColor="#167bff" />
          <stop offset="1" stopColor="#b45cff" />
        </linearGradient>
        <filter id="cp-logo-cyan-glow" x="-40%" y="-55%" width="180%" height="210%">
          <feGaussianBlur stdDeviation="5" result="blur" />
          <feColorMatrix in="blur" type="matrix" values="0 0 0 0 0.05 0 0 0 0 0.85 0 0 0 0 1 0 0 0 .75 0" result="glow" />
          <feMerge>
            <feMergeNode in="glow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <rect x="15" y="10" width="90" height="62" rx="18" fill="#06101a" stroke="url(#cp-logo-shell)" strokeWidth="4" filter="url(#cp-logo-cyan-glow)" />
      <text x="60" y="52" textAnchor="middle" fill="url(#cp-logo-text)" fontFamily="Inter, Arial, sans-serif" fontSize="36" fontWeight="950" letterSpacing="-2">CP</text>
      <path d="M33 62h54" stroke="#31eaff" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function buildDataStatus(status: DataStatusPayload | null, hasError: boolean) {
  if (hasError || !status?.latestUpdate) return { label: "Unavailable", tone: "bad", detail: "Market data status could not be loaded." };
  const date = parseServerTimestamp(status.latestUpdate);
  if (!date) return { label: status.latestUpdate, tone: "bad", detail: "The server update timestamp is invalid." };

  const ageMs = Date.now() - date.getTime();
  const counts = status.freshnessCounts ?? {};
  const hasStale = (counts.stale ?? 0) + (counts.offline ?? 0) > 0;
  const hasCached = (counts.cached ?? 0) > 0;
  const recencyTone = ageMs <= 60 * 60 * 1000 ? "good" : ageMs <= 24 * 60 * 60 * 1000 ? "warn" : "bad";
  const tone = hasStale ? "bad" : hasCached && recencyTone === "good" ? "warn" : recencyTone;
  const detail = `Market timestamps: ${counts.delayed ?? 0} current session, ${counts.cached ?? 0} one session behind, ${counts.stale ?? 0} stale, ${counts.offline ?? 0} offline.`;
  return { label: formatKst(date), tone, detail };
}

function parseServerTimestamp(value: string) {
  const normalized = value.includes("T") ? value : `${value.replace(" ", "T")}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatKst(date: Date) {
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).format(date).replace("T", " ");
}
